import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, BarChart3, CheckCircle2, Circle, FileText, Loader2, ShieldCheck, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { CompanyStatus, ROLE_LABEL } from "@/pages/Companies";
import { FileField } from "@/components/FileField";
import { errMsg, fileUrl, useApi } from "@/lib/api";

const LABELS = {
  razon_social: "Razón social", rut: "RUT", nombre_establecimiento: "Nombre del establecimiento", id_establecimiento_vu: "ID establecimiento (Ventanilla Única)",
  direccion: "Dirección", comuna: "Comuna", region: "Región", actividad_economica: "Actividad económica", codigo_ciiu: "Código CIIU",
  representante_legal: "Representante legal", correo: "Correo", telefono: "Teléfono",
  estado_retc: "Estado registro RETC", fecha_estado_retc: "Fecha del comprobante RETC", encargado_establecimiento: "Encargado del establecimiento", sistemas_retc: "Sistemas sectoriales y roles activos (RETC)",
  estado_declaracion: "Estado declaración SINADER / SIDREP", sanciones_activas: "Sanciones activas (SNIFA)",
  numero_resolucion: "N° Resolución Sanitaria", cve_resolucion: "CVE Resolución", vigencia_resolucion: "Vigencia Resolución",
  patentes_autorizadas: "Patentes autorizadas (Resolución de Transporte)", estado_registro_transportista: "Estado Registro de Transportistas RETC",
};

const RETC_OWNED = ["id_establecimiento_vu", "estado_retc", "fecha_estado_retc", "encargado_establecimiento", "sistemas_retc"];

const Summary = ({ r, setR, locked }) => (
  <section className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8" data-testid="company-summary">
    <h2 className="font-semibold text-lg text-[#05130e] mb-5">Datos principales</h2>
    <div className="grid sm:grid-cols-2 gap-4">
      {Object.entries(LABELS).map(([k, label]) => (
        <label key={k} className="space-y-1.5">
          <span className="text-xs text-slate-500">{label}</span>
          {locked || RETC_OWNED.includes(k) ? <p className="font-medium text-[#05130e]" data-testid={`company-summary-${k}`}>{r[k] || "—"}</p>
            : <Input className="h-10" value={r[k] || ""} onChange={(e) => setR({ ...r, [k]: e.target.value })} data-testid={`company-summary-${k}-input`} />}
        </label>
      ))}
    </div>
  </section>
);

const DocFields = ({ campos }) => {
  const sections = [...new Set(campos.map((c) => c.seccion || "General"))];
  return (
    <details className="mt-3">
      <summary className="cursor-pointer text-sm text-[#00915f] font-medium">Ver todos los datos leídos ({campos.length})</summary>
      {sections.map((s) => (
        <div key={s} className="mt-3">
          <p className="font-mono text-[11px] uppercase tracking-wider text-[#00915f] mb-1">{s}</p>
          <dl className="divide-y divide-slate-100 border border-slate-100 rounded-xl">
            {campos.filter((c) => (c.seccion || "General") === s).map((c, i) => (
              <div key={i} className="grid sm:grid-cols-5 gap-2 p-2.5 text-sm"><dt className="sm:col-span-2 text-slate-500">{c.etiqueta}</dt><dd className="sm:col-span-3 font-medium text-[#05130e] break-words">{c.valor || "—"}</dd></div>
            ))}
          </dl>
        </div>
      ))}
    </details>
  );
};

const DocItem = ({ c, req, onUploaded, locked, lock, setLock }) => {
  const api = useApi();
  const busy = lock === req.type;
  const d = c.documents?.[req.type];
  const upload = async (fileId) => {
    if (!fileId) return;
    setLock(req.type);
    try {
      const { data } = await api("post", `/companies/${c.id}/documents`, { doc_type: req.type, file_id: fileId });
      toast.success("Documento leído y guardado");
      onUploaded(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLock(null);
    }
  };
  return (
    <li className="border border-slate-200 rounded-2xl p-4" data-testid={`company-doc-${req.type}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="flex items-center gap-2 font-medium text-[#05130e]">
          {d ? <CheckCircle2 className="w-5 h-5 text-[#00b37a]" /> : <Circle className="w-5 h-5 text-slate-300" />}{req.label}
        </p>
        {busy ? <span className="flex items-center gap-2 text-sm text-slate-500" data-testid={`company-doc-${req.type}-reading`}><Loader2 className="w-4 h-4 animate-spin" />Leyendo el PDF...</span>
          : !locked && !lock && <FileField value={null} onChange={upload} accept="application/pdf" label={d ? "Reemplazar PDF" : "Subir PDF"} testId={`company-doc-${req.type}-upload`} />}
      </div>
      {d && (
        <>
          <a href={fileUrl(d.file_id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:underline mt-1"><FileText className="w-3.5 h-3.5" />{d.file_name}</a>
          {!d.expected && <p className="text-xs text-amber-700 mt-1" data-testid={`company-doc-${req.type}-unexpected`}>Este PDF no parece ser el documento solicitado. Revísalo.</p>}
          <DocFields campos={d.campos || []} />
        </>
      )}
    </li>
  );
};

const Fields = ({ c, onUploaded, locked }) => {
  const [lock, setLock] = useState(null);
  return (
    <section className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 space-y-4" data-testid="company-fields">
      <h2 className="font-semibold text-lg text-[#05130e]">Documentos requeridos para {ROLE_LABEL[c.role]}</h2>
      {lock && <p className="text-xs text-slate-500" data-testid="company-doc-wait-notice">Espera a que termine la lectura para subir el siguiente documento.</p>}
      <ul className="space-y-3">{c.required_docs.map((req) => <DocItem key={req.type} c={c} req={req} onUploaded={onUploaded} locked={locked} lock={lock} setLock={setLock} />)}</ul>
    </section>
  );
};

export default function CompanyReview() {
  const { id } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [r, setR] = useState({});
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const dirty = useRef({});

  const load = (d) => { setC(d); setR(d.resumen || {}); dirty.current = {}; };
  const onUploaded = (d) => {
    setC(d);
    setR((prev) => ({ ...(d.resumen || {}), ...Object.fromEntries(Object.keys(dirty.current).map((k) => [k, prev[k]])) }));
  };
  const editR = (next) => {
    Object.keys(next).forEach((k) => { if (next[k] !== r[k]) dirty.current[k] = true; });
    setR(next);
  };
  useEffect(() => {
    api("get", `/companies/${id}`).then((x) => load(x.data)).catch(() => navigate("/mis-empresas"));
  }, [api, id, navigate]);

  const validate = async () => {
    setBusy(true);
    try {
      await api("put", `/companies/${id}`, { resumen: r });
      const { data } = await api("post", `/companies/${id}/validate`, { confirm: true });
      load(data);
      toast.success("Empresa validada. Ya puedes publicar lotes con ella.");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("¿Descartar esta empresa y sus datos leídos?")) return;
    try {
      await api("delete", `/companies/${id}`);
      toast.success("Empresa descartada");
      navigate("/mis-empresas");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (!c) return <main className="max-w-4xl mx-auto px-5 py-12"><Skeleton className="h-96 rounded-3xl" /></main>;
  const locked = c.status === "validada";

  return (
    <main className="max-w-4xl mx-auto px-5 lg:px-8 py-10 space-y-6" data-testid="company-review-page">
      <Link to="/mis-empresas" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#05130e]"><ArrowLeft className="w-4 h-4" />Mis empresas</Link>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#00b37a]">{ROLE_LABEL[c.role]}</p>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#05130e]" data-testid="company-review-title">{r.razon_social || "Sube los documentos de la empresa"}</h1>
        </div>
        <CompanyStatus status={c.status} />
      </div>
      {locked && c.role === "generador" && (
        <Link to={`/mis-empresas/${id}/gri-306`} className="flex items-center justify-between gap-4 bg-[#0a1f18] text-white rounded-3xl p-5 hover:bg-[#0f2c22] transition-colors" data-testid="company-gri-link">
          <span className="flex items-center gap-3"><BarChart3 className="w-6 h-6 text-[#00f2a1]" /><span><span className="block font-semibold">Informe GRI 306</span><span className="block text-xs text-slate-400">Residuos generados, desviados y eliminados · Tasa de Desviación Circular</span></span></span>
          <span className="text-sm text-[#00f2a1] font-semibold">Abrir →</span>
        </Link>
      )}
      <Fields c={c} onUploaded={onUploaded} locked={locked} />
      <Summary r={r} setR={editR} locked={locked} />
      {locked ? (
        <p className="flex items-center gap-2 text-sm text-[#007a50] bg-[#00f2a1]/10 rounded-2xl p-4" data-testid="company-validated-note"><ShieldCheck className="w-5 h-5" />Validada el {new Date(c.validated_at).toLocaleString("es-CL", { dateStyle: "medium", timeStyle: "short" })}. Ya aparece al publicar un lote.</p>
      ) : (
        <section className="bg-[#0a1f18] text-white rounded-3xl p-6 sm:p-8 space-y-5" data-testid="company-validate-section">
          <label className="flex items-start gap-3 text-sm">
            <Checkbox checked={ok} onCheckedChange={(v) => setOk(!!v)} className="mt-0.5 border-[#00f2a1]" data-testid="company-confirm-checkbox" />
            Confirmo que revisé los datos y que son correctos y corresponden a los documentos oficiales emitidos (SII, Ministerio del Medio Ambiente, SMA y SEREMI de Salud).
          </label>
          <div className="flex flex-wrap justify-between gap-3">
            <Button variant="ghost" onClick={remove} className="text-slate-300 hover:text-white hover:bg-white/10" data-testid="company-delete-btn"><Trash2 className="w-4 h-4" />Descartar</Button>
            <Button disabled={!ok || busy || c.status !== "pendiente_validacion"} onClick={validate} className="rounded-full px-6 font-semibold disabled:bg-slate-600 disabled:text-slate-300 disabled:opacity-100" data-testid="company-validate-btn"><ShieldCheck className="w-4 h-4" />{busy ? "Validando..." : "Validar empresa"}</Button>
          </div>
        </section>
      )}
    </main>
  );
}
